import { createFileRoute, redirect } from "@tanstack/react-router";
import { authClient } from "@/lib/auth";
import { useConfig } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Globe, Cookie, Server } from "lucide-react";

export const Route = createFileRoute("/settings")({
  component: SettingsPage,
  beforeLoad: async () => {
    const { data } = await authClient.getSession();
    if (!data) {
      throw redirect({ to: "/login" });
    }
  },
});

function SettingsPage() {
  const { data: configRes } = useConfig();
  const config = configRes?.config;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Auth service configuration.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="size-4" />
              Base Configuration
            </CardTitle>
            <CardDescription>Core auth service settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-sm font-medium">Base URL</p>
                <p className="text-xs text-muted-foreground">Auth service origin</p>
              </div>
              <span className="rounded-md bg-muted px-2 py-1 font-mono text-xs">
                {config?.baseUrl ?? "—"}
              </span>
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-sm font-medium">Auth Path</p>
                <p className="text-xs text-muted-foreground">Better Auth base path</p>
              </div>
              <span className="rounded-md bg-muted px-2 py-1 font-mono text-xs">
                {config?.basePath ?? "—"}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cookie className="size-4" />
              Cookies
            </CardTitle>
            <CardDescription>Cross-subdomain cookie settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="space-y-0.5">
                <Label>Cross-subdomain cookies</Label>
                <p className="text-xs text-muted-foreground">
                  Share sessions across subdomains
                </p>
              </div>
              <Switch checked={config?.crossSubDomainCookies.enabled ?? false} disabled />
            </div>
            {config?.crossSubDomainCookies.enabled && (
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <p className="text-sm font-medium">Cookie Domain</p>
                  <p className="text-xs text-muted-foreground">Domain attribute value</p>
                </div>
                <span className="rounded-md bg-muted px-2 py-1 font-mono text-xs">
                  {config.crossSubDomainCookies.domain ?? "—"}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="size-4" />
              Trusted Origins
            </CardTitle>
            <CardDescription>Origins allowed to interact with the auth API</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {config?.trustedOrigins.map((origin) => (
                <span
                  key={origin}
                  className="rounded-full bg-muted px-3 py-1 text-xs font-medium"
                >
                  {origin}
                </span>
              )) ?? <span className="text-sm text-muted-foreground">Loading…</span>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
